import { useEffect } from 'react';

import { getAppGlobals } from '~/components/AppGlobals/utils';

/**
 * Determines if an error should be logged to the advisory project instead of main.
 * Advisory errors are informational only - caused by browser extensions, 3rd party scripts,
 * or other external factors we can't control.
 *
 * These include:
 * - DOM manipulation errors from browser extensions (removeChild, appendChild, insertBefore)
 * - NotFoundError during DOM operations
 * - Other transient/external errors that shouldn't count as application bugs
 *
 * NOTE: Errors tagged with 'persisted-after-reload' are NOT advisory - they're real bugs
 * that survived a page reload and need investigation.
 */
export function isAdvisoryError(error: unknown, sentryEvent?: { tags?: Record<string, string> }): boolean {
  if (!(error instanceof Error)) return false;

  // If this error persisted after reload, it's NOT advisory - it's a real bug
  if (sentryEvent?.tags?.['persisted-after-reload'] === 'true') {
    return false;
  }

  // DOM manipulation errors from browser extensions
  const isDOMManipulation =
    error.message.includes('removeChild') ||
    error.message.includes('appendChild') ||
    error.message.includes('insertBefore') ||
    error.name === 'NotFoundError';

  return isDOMManipulation;
}

/**
 * Hook to clean up advisory error reload flag on successful page load.
 * This allows a fresh reload attempt if another advisory error occurs later.
 * Should be called in the root App component.
 */
export function useAdvisoryErrorCleanup(): void {
  useEffect(() => {
    const reloadKey = 'advisory-error-reload-attempted';
    sessionStorage.removeItem(reloadKey);
  }, []);
}

/**
 * Parses a DSN string into its components
 */
function parseDSN(dsn: string): { publicKey: string; host: string; projectId: string } | null {
  const match = dsn.match(/https:\/\/(.+)@(.+)\/(\d+)/);
  if (!match) return null;
  const [, publicKey, host, projectId] = match;
  return { publicKey, host, projectId };
}

/**
 * Generates a unique event ID for Sentry
 */
function generateEventId(): string {
  return 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/x/g, () => {
    return Math.floor(Math.random() * 16).toString(16);
  });
}

/**
 * Captures advisory errors (DOM manipulation, browser extension interference, etc.)
 * to a separate Sentry project to avoid cluttering the main error log.
 *
 * These errors are informational only - we can't prevent them, but we want visibility
 * for debugging and understanding user environments.
 */
export function captureAdvisoryError(
  error: Error,
  context?: {
    tags?: Record<string, string>;
    contexts?: Record<string, unknown>;
    extra?: Record<string, unknown>;
  },
) {
  const appGlobals = getAppGlobals();

  // If no advisory DSN is configured, don't send anywhere
  // Advisory errors are informational only and would be filtered out by beforeSend anyway
  if (!appGlobals.SENTRY_ADVISORY_DSN) {
    return;
  }

  // Send directly to advisory Sentry project using fetch
  // We use direct API calls instead of BrowserClient because Sentry's SDK
  // doesn't make it easy to send to multiple projects - the multi-client
  // approach with withIsolationScope doesn't reliably trigger network requests.
  // This approach gives us full control and is more reliable.
  try {
    const dsnParts = parseDSN(appGlobals.SENTRY_ADVISORY_DSN);
    if (!dsnParts) {
      console.error('[ADVISORY] Invalid DSN format');
      return;
    }

    const { publicKey, host, projectId } = dsnParts;
    const endpoint = `https://${host}/api/${projectId}/envelope/`;

    // Build Sentry event payload
    // Using SDK's event format to ensure compatibility
    const event = {
      event_id: generateEventId(),
      timestamp: Date.now() / 1000,
      platform: 'javascript',
      level: 'warning',
      exception: {
        values: [
          {
            type: error.name,
            value: error.message,
            stacktrace: error.stack
              ? {
                  frames: parseStackTrace(error.stack),
                }
              : undefined,
          },
        ],
      },
      tags: {
        keyword: 'advisory-error',
        'orchid-version': '2',
        ...context?.tags,
      },
      contexts: context?.contexts || {},
      extra: context?.extra || {},
      environment: appGlobals.SENTRY_ENV,
      release: appGlobals.SENTRY_RELEASE || 'local',
    };

    // Create Sentry envelope format
    const envelope = createEnvelope(event);

    // Send with fetch - fire and forget
    // Some events might not make it if reload is too fast, but that's okay
    // These are advisory only - informational, not critical
    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-sentry-envelope',
        'X-Sentry-Auth': `Sentry sentry_version=7,sentry_key=${publicKey},sentry_client=orchid-advisory/2.0.0`,
      },
      body: envelope,
      keepalive: true, // Hint to browser: try to complete this even if page unloads
    }).catch(() => {
      // Ignore errors - advisory only, fire and forget
    });
  } catch (err) {
    console.error('[ADVISORY] Error sending advisory error:', err);
  }
}

/**
 * Parses a stack trace string into Sentry's frame format
 */
function parseStackTrace(stack: string): Array<{
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
}> {
  const lines = stack.split('\n').slice(1); // Skip first line (error message)
  return lines
    .map((line) => {
      // Match: "at functionName (filename:line:col)"
      const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);
      if (match) {
        return {
          function: match[1],
          filename: match[2],
          lineno: Number.parseInt(match[3], 10),
          colno: Number.parseInt(match[4], 10),
        };
      }
      // Match: "at filename:line:col"
      const simpleMatch = line.match(/at\s+(.+?):(\d+):(\d+)/);
      if (simpleMatch) {
        return {
          filename: simpleMatch[1],
          lineno: Number.parseInt(simpleMatch[2], 10),
          colno: Number.parseInt(simpleMatch[3], 10),
        };
      }
      return null;
    })
    .filter((frame): frame is NonNullable<typeof frame> => frame !== null);
}

/**
 * Creates a Sentry envelope from an event
 * Envelope format: header\nitem_header\npayload
 */
function createEnvelope(event: Record<string, unknown>): string {
  const header = JSON.stringify({
    event_id: event.event_id,
    sent_at: new Date().toISOString(),
  });
  const itemHeader = JSON.stringify({
    type: 'event',
    content_type: 'application/json',
  });
  const payload = JSON.stringify(event);

  return `${header}\n${itemHeader}\n${payload}`;
}
